// ════════════════════════════════════════════════════════════
//  online/challengers.js — CHALLENGERS: players and partners in the League, for real
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-21 (replacing the 2026-10-20 GUESTS, whose bouts never counted):
//   "once you join you're official so she should be in the rankings too" ·
//   "players can just jump right in at any point … they're just in after joining, official <3" ·
//   "if player/partner is eligible for the champions cup because they won enough etc, they must sign in
//    the 10 minutes prior so the tournament has time to register all proper/eligible names" ·
//   the Losers' Cup: "if an actual player is playing all week and they didn't make it to the Champion's
//    Cup they can still join the Loser's cup free for all".
//
//  A CHALLENGER is a player or a partner (partners are treated the same as players) with a League key
//  `ch_{uid}`. Their bouts are real: they go in the ledger, move the table, the ratings and the Wire.
//
//  league/members/{uid}   { n, at, p }       JOIN — once, official from then on. A new member starts in
//                                            the Contenders (from the next card on) and rises like anyone:
//                                            Friday's Apocalypse.
//  league/ready/{uid}     { at, t, tm, n, p } "I'M IN" — on every card that goes up while it's fresh
//                                            (READY_FOR). The screen re-stamps it while the switch is on.
//                                            A card is written once (the lock), so whoever it caught is caught.
//  league/entries/{c}_{day}/{uid} { at, t, tm, n, p } A CUP CHECK-IN (from V3 a cup night's five cups share ONE: c = "n", up to 10:49am) — counts only if the SERVER stamped it
//                                            in the CHECKIN_MS before the cup's first card goes up (:40–:50).
//                                            Written once (a transaction), so every hour of the cup draws the
//                                            same field.
//  league/paid/{uid}/{rowId}                  King's Krests already paid for a win (paid once, ever).
//
//  t = their three (roster line ids) · tm = their Tomes · n = their name · p = has a partner (a no-show's
//  partner fights for them — the AI takeover; with no partner it's a forfeit, the lockstep no-show rule).
//  ⚠ TESTING-PHASE TRUST (same as bets and results): any signed-in screen can write here (the
//  games/$gameId umbrella in rules.json — no rules paste needed).
// ════════════════════════════════════════════════════════════
import { BASE } from "./ie.js";
import { LINES } from "../data/roster.js";
import { TOME_IDS } from "../data/arts.js";
import { lockAtOf, cutOf, CHALLENGERS, chKey, isChKey, uidOfKey, hourInfo, weekOfDay, checkInsOfDay, entryCupOf, dayIsV3, hourStart, hoursOfWeek } from "./circuit.js";

export { chKey, isChKey, uidOfKey };
export const CPATH = `${BASE}/league`;   // = ledger.js CPATH (not imported: ledger.js imports this file)
export const membersPath = `${CPATH}/members`;
export const readyPath = `${CPATH}/ready`;
export const entriesPath = (c, day) => `${CPATH}/entries/${c}_${day}`;
export const paidPath = (uid) => `${CPATH}/paid/${uid}`;

export const READY_FOR = 15 * 60e3;          // ⟵ DIAL — an "I'm in" this fresh puts you on the next card
export const READY_EVERY = 4 * 60e3;          // ⟵ DIAL — how often a screen with the switch on re-stamps it
export const CHECKIN_MS = 10 * 60e3;          // ⟵ DIAL — caP'n: "they must sign in the 10 minutes prior"
export const CH_PURSE = 1;                    // ⟵ DIAL — King's Krests for a Challenger's win (caP'n: "KK 1 for now is fine")

export const validTeam = (t) => Array.isArray(t) && t.length === 3 && new Set(t).size === 3 && t.every((x) => typeof x === "string" && LINES[x]);
export const cleanTomes = (tm) => (Array.isArray(tm) && tm.length && tm.every((x) => TOME_IDS.includes(x)) ? tm.slice(0, 3) : null);
const ts = (net, now) => (net.TS ? net.TS() : now);

// ── WHO THEY ARE ─────────────────────────────────────────────
/** Everyone who ever joined: Map(key → { key, uid, name, at, partner }). Also fills circuit.js CHALLENGERS (names for every screen). */
export async function membersOf(net, { strict = false } = {}) {
  // strict (a lock is being written — forever): a failed read is a FAILURE, never "nobody joined"
  const raw = (strict ? await net.get(membersPath) : await net.get(membersPath).catch(() => null)) || {};
  const out = new Map();
  for (const [uid, m] of Object.entries(raw)) {
    if (!m || typeof m.at !== "number") continue;
    const key = chKey(uid), v = { key, uid, name: String(m.n || "A Challenger").slice(0, 24), at: m.at, partner: !!m.p, t: validTeam(m.t) ? m.t.slice(0, 3) : null, tm: cleanTomes(m.tm) };
    out.set(key, v);
    CHALLENGERS.set(key, v);
  }
  return out;
}
/** JOIN: once, forever. Resolves { ok, already } (never throws). */
export async function join(net, { uid, name, partner = false, now = Date.now() }) {
  if (!uid) return { ok: false, reason: "Sign in to join the League." };
  const me = { n: String(name || "A Challenger").slice(0, 24), at: ts(net, now), p: partner ? 1 : 0 };
  const r = await net.txn(`${membersPath}/${uid}`, (cur) => (cur == null ? me : undefined)).catch(() => null);
  if (!r) return { ok: false, reason: "Couldn't join — try again." };
  CHALLENGERS.set(chKey(uid), { key: chKey(uid), uid, name: me.n, at: now, partner: !!partner });
  return { ok: true, already: !r.committed };
}
/**
 * A week's league as the card at hour h sees it: the stored lists + every member who had joined before the card
 * went up and isn't on a list yet (they start in the Contenders). Pure.
 */
export function withMembers(league, members, h) {
  if (!league) return league;
  league = league.base || league;   // (a League already "on a day" — circuit.js leagueOn — starts again from the week's own lists)
  const on = new Set([...league.circuit, ...league.contenders]);
  const cut = lockAtOf(h);
  const fresh = [...members.values()].filter((m) => !on.has(m.key) && m.at < cut).sort((a, b) => a.at - b.at || (a.key < b.key ? -1 : 1)).map((m) => m.key);
  return fresh.length ? { ...league, contenders: [...league.contenders, ...fresh] } : league;
}

/** A member's LAST three (and Tomes) — what they fight with when a bout they can't skip finds them away
 *  (Apocalypse, a cup round on paper): their partner or a forfeit then decides it, never a scratched bout that stalls a series. */
const rememberThree = (net, uid, v) => Promise.all([net.set(`${membersPath}/${uid}/t`, v.t), net.set(`${membersPath}/${uid}/tm`, v.tm || null)]).catch(() => {});
/** Entrant data for a member from what they last fought with (null if they never picked a three). */
export const memberData = (m) => (m && validTeam(m.t) ? { uid: m.uid, n: m.name, t: m.t.slice(0, 3), ...(m.tm ? { tm: m.tm } : {}), p: m.partner ? 1 : 0 } : null);

// ── "I'M IN" ─────────────────────────────────────────────────
/** Switch "I'm in" on (with your three) or off. Resolves { ok, reason? }. */
export async function setReady(net, { uid, on, team, tomes = null, name, partner = false, now = Date.now() }) {
  if (!uid) return { ok: false, reason: "Sign in first." };
  if (!on) { await net.set(`${readyPath}/${uid}`, null).catch(() => {}); return { ok: true }; }
  if (!validTeam(team)) return { ok: false, reason: "Pick your three first." };
  const v = { at: ts(net, now), t: team.slice(0, 3), ...(cleanTomes(tomes) ? { tm: cleanTomes(tomes) } : {}), n: String(name || "A Challenger").slice(0, 24), p: partner ? 1 : 0 };
  const ok = await net.set(`${readyPath}/${uid}`, v).then(() => true).catch(() => false);
  if (ok) await rememberThree(net, uid, v);
  return ok ? { ok: true } : { ok: false, reason: "Couldn't save — try again." };
}
/** Who is on hour h's card: Map(key → entrant data) — every member whose "I'm in" was fresh when the card went up. */
export function readyFor(ready, members, h) {
  const out = new Map(), since = lockAtOf(h) - READY_FOR;
  for (const [uid, v] of Object.entries(ready || {})) {
    const key = chKey(uid), m = members.get(key);
    if (!m || !v || typeof v.at !== "number" || v.at < since || !validTeam(v.t)) continue;
    out.set(key, dataOf(uid, v, m));
  }
  return out;
}
const dataOf = (uid, v, m) => ({ uid, n: String(v.n || (m && m.name) || "A Challenger").slice(0, 24), t: v.t.slice(0, 3), ...(cleanTomes(v.tm) ? { tm: cleanTomes(v.tm) } : {}), p: v.p ? 1 : 0 });

// ── CUP CHECK-IN ─────────────────────────────────────────────
/** When a cup's check-in is open: [open, close) — the CHECKIN_MS before its first card goes up. null: no such cup. */
export function checkInWindow(c, day) {
  const cut = cutOf(c, day);
  if (cut == null) return null;
  // V3: shut a minute BEFORE the :50 lock, so a check-in made at the last second has landed before any card reads the entries
  // (the Qualifier hours read them at 10:50 and again at 11:50 and 12:50 — all three must see the same list)
  const close = lockAtOf(cut) - (dayIsV3(day) ? 60e3 : 0);
  // caP'n 2026-10-22: "any time, all cups too you can register early of course" — from the cup night's own week start (V3);
  // before it, the 10 minutes before the card goes up
  const open = dayIsV3(day) ? hourStart(hoursOfWeek(weekOfDay(day))[0]) : close - CHECKIN_MS;
  return { open, close, cut };
}
const entryCounts = (e, w) => !!e && typeof e.at === "number" && e.at >= w.open && e.at < w.close && validTeam(e.t);
/** Check in for a cup (once). Resolves { ok, reason? }. The SERVER's stamp decides — one made late counts on no screen. */
export async function checkIn(net, { c, day, uid, team, tomes = null, name, partner = false, now = Date.now() }) {
  const w = checkInWindow(c, day);
  if (!uid) return { ok: false, reason: "Sign in first." };
  if (!w) return { ok: false, reason: "There's no such cup." };
  if (now < w.open || now >= w.close) return { ok: false, reason: now >= w.close ? "Check-in closed — the brackets are up." : "Check-in opens when the week starts." };
  if (!validTeam(team)) return { ok: false, reason: "Pick your three first." };
  const v = { at: ts(net, now), t: team.slice(0, 3), ...(cleanTomes(tomes) ? { tm: cleanTomes(tomes) } : {}), n: String(name || "A Challenger").slice(0, 24), p: partner ? 1 : 0 };
  const r = await net.txn(`${entriesPath(c, day)}/${uid}`, (cur) => (cur == null ? v : undefined)).catch(() => null);
  if (!r) return { ok: false, reason: "Couldn't check in — try again." };
  if (r.committed) await rememberThree(net, uid, v);
  return { ok: true, already: !r.committed };
}
/** A cup's valid check-ins: Map(key → entrant data). */
export async function entriesOf(net, c, day, members, { strict = false } = {}) {
  const w = checkInWindow(c, day), out = new Map();
  if (!w) return out;
  const raw = (strict ? await net.get(entriesPath(c, day)) : await net.get(entriesPath(c, day)).catch(() => null)) || {};
  for (const [uid, e] of Object.entries(raw)) {
    const key = chKey(uid);
    // only someone who had JOINED before the cup's card went up (so every hour of the cup sees the same members)
    if (members && !(members.has(key) && members.get(key).at < w.close)) continue;
    if (entryCounts(e, w)) out.set(key, dataOf(uid, e, members && members.get(key)));
  }
  return out;
}

// ── THE LEAGUE AS A CARD SEES IT ─────────────────────────────
// Every place that plans a card or draws a bracket (stage.js locks, paper hours, ledger.js's week turn, the boards) reads the
// League through THIS, so they can never disagree: the stored lists + the members who had joined before the card went up,
// and the check-ins of every cup of the week that's been drawn by hour h (Saturday's Champions Cup redraws the week's
// nightly brackets to find their champions — it needs their check-ins too).
// Reads are cached once they can't change any more (game time `now` well past the moment that decides them), so a
// fast-forward through hundreds of hours doesn't re-read the same nodes.
const memo = new WeakMap();
const cacheOf = (net) => { let m = memo.get(net); if (!m) { m = { members: null, entries: new Map() }; memo.set(net, m); } return m; };
const SETTLE = 60e3;
async function membersAt(net, h, now, strict) {
  const C = cacheOf(net);
  if (C.members && C.members.readAt >= lockAtOf(h) + SETTLE) return C.members.map;
  const map = await membersOf(net, { strict });
  // only a read that can't have quietly failed is ever kept (a board's blip must never become a lock's "nobody")
  if (strict && now >= lockAtOf(h) + SETTLE) C.members = { readAt: now, map };
  return map;
}
async function entriesAt(net, c, day, members, now, strict) {
  const C = cacheOf(net), k = `${c}_${day}`, w = checkInWindow(c, day);
  if (C.entries.has(k)) return C.entries.get(k);
  const E = await entriesOf(net, c, day, members, { strict });
  if (strict && w && now >= w.close + SETTLE) C.entries.set(k, E);
  return E;
}
/**
 * The League for hour h's card. stored = the week's stored league (ledger.js leagueOf). strict: a lock is being written —
 * any failed read throws (the caller doesn't lock), never quietly "nobody".
 * Resolves { league, members, entered(c, day, key), cupData(c, day, key) }.
 */
export async function leagueCtx(net, h, stored, now, { strict = false } = {}) {
  const members = await membersAt(net, h, now, strict);
  const none = { league: stored, members, entered: null, cupData: () => null };
  if (!stored || !members.size) return none;
  const league = withMembers(stored, members, h), info = hourInfo(h), byCup = new Map();
  // V3 (2026-10-22): a cup night is ONE check-in ("n" — all five cups); Saturday's are their own (checkInsOfDay)
  for (let d = weekOfDay(info.day) * 7 - 4; d <= info.day; d++) {
    for (const c of checkInsOfDay(d)) {
      const cut = cutOf(c, d);
      if (cut == null || cut > h) continue;   // not drawn yet: its check-ins don't matter
      byCup.set(`${c}_${d}`, await entriesAt(net, c, d, members, now, strict));
    }
  }
  return {
    league, members,
    entered: (c, d, k) => { const x = byCup.get(`${entryCupOf(c, d)}_${d}`); return !!(x && x.has(k)); },
    cupData: (c, d, k) => { const x = byCup.get(`${entryCupOf(c, d)}_${d}`); return (x && x.get(k)) || null; },
  };
}

// ── A CHALLENGER ON THE CARD ─────────────────────────────────
/** lock.ch[key] → an entrant (stage.js). */
export const challengerEntrant = (key, d) => (d && validTeam(d.t) ? {
  key, name: String(d.n || "A Challenger").slice(0, 24), team: d.t.slice(), tomes: cleanTomes(d.tm), home: "challenger", uid: d.uid || uidOfKey(key), partner: !!d.p, style: null, change: null,
} : null);

// ── KING'S KRESTS ────────────────────────────────────────────
/**
 * Pay my wins: every ledger row of these weeks I won as a Challenger, ONCE (a transaction claims each row first,
 * so two tabs never pay twice). rowsOf(w) → rows. pay(amount) → new balance or null (refused). Resolves the total paid.
 */
export async function collectChallengerPay(net, uid, weeks, rowsOf, pay) {
  if (!uid) return 0;
  const key = chKey(uid), done = (await net.get(paidPath(uid)).catch(() => null)) || {};
  let paid = 0;
  for (const w of weeks) {
    for (const r of await rowsOf(w)) {
      if (!r || done[r.id] != null) continue;
      const side = r.a === key ? 0 : r.b === key ? 1 : -1;
      if (side < 0 || r.w !== side) continue;
      const c = await net.txn(`${paidPath(uid)}/${r.id}`, (cur) => (cur == null ? "paying" : undefined)).catch(() => null);
      if (!c || !c.committed) continue;
      let after = null;
      try { after = await pay(CH_PURSE); } catch { after = null; }
      if (after == null) { await net.set(`${paidPath(uid)}/${r.id}`, null).catch(() => {}); continue; }
      paid += CH_PURSE;
      await net.set(`${paidPath(uid)}/${r.id}`, CH_PURSE).catch(() => {});
    }
  }
  return paid;
}
